import {
  Mesh,
  DoubleSide,
  MeshBasicMaterial,
  PlaneGeometry,
} from 'three';

import { VRObject3D } from './VRObject3D';
import { musicObjectTexture } from './helpers/texturesAndGeometries';

const PAGE_MEDIA_AUDIO_ROOT_ID = 'page-media-audio-root';

const getPageMediaAudioRoot = () => {
  if (typeof document === 'undefined') return null;
  let root = document.getElementById(PAGE_MEDIA_AUDIO_ROOT_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = PAGE_MEDIA_AUDIO_ROOT_ID;
    root.setAttribute('aria-hidden', 'true');
    root.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none';
    document.body.appendChild(root);
  }
  return root;
};

class VRMusicObject extends VRObject3D {
  _syncVisibilityForMode() {
    const inEditMode = !!this.model?.editMode;
    this.mesh.visible = inEditMode;
    this.material.opacity = inEditMode ? 1 : 0;
    this.material.needsUpdate = true;
  }

  constructor(containerName, camera, texture, x, y, z, model) {
    super(containerName);

    this.camera = camera;
    this.model = model;
    this.audioEl = null;
    this._musicPlaybackActive = false;

    this.material = new MeshBasicMaterial({
      side: DoubleSide,
      transparent: true,
      map: texture || musicObjectTexture,
      opacity: model?.editMode ? 1 : 0,
    });

    this.geometry = new PlaneGeometry(64, 64);
    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.type = 'vrMusicObject';
    this.mesh.name = 'vrMusicObject';
    this.name = 'vrMusicObject';
    this.type = 'vrMusicObject';
    this.mesh.visible = false;

    this.limits = {
      min: 20,
      max: 600,
      distance: 300,
    };

    this.camera !== null ? this._setInitialPosRot() : this.setPosition(x, y, z);
    this.add(this.mesh);

    this.mesh.groupDetails = {
      title: $trans('vr-music-object'),
      icon: 'music_note',
      editTitle: $trans('edit-vr-music-object'),
      dataLang: 'edit-vr-music-object',
    };

    this.editControls();
    this._syncVisibilityForMode();
    return this;
  }

  _ensureAudioElement() {
    if (this.audioEl) {
      const root = getPageMediaAudioRoot();
      if (root && !this.audioEl.isConnected) {
        root.appendChild(this.audioEl);
      }
      return this.audioEl;
    }
    if (typeof document === 'undefined') return null;
    const audio = document.createElement('audio');
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';
    audio.className = 'page-media-audio vr-music-object-audio';
    const root = getPageMediaAudioRoot();
    if (root && !audio.isConnected) {
      root.appendChild(audio);
    }
    this.audioEl = audio;
    if (this.model?.pageMediaMuted) {
      this.applyPageMediaMute(true);
    }
    return audio;
  }

  applyPageMediaMute(muted) {
    const audio = this.audioEl || this._ensureAudioElement();
    if (!audio) return;
    if (muted) {
      if (!audio.dataset.pageMutedByToggle) {
        audio.dataset.pageMutedByToggle = '1';
      }
      audio.muted = true;
      return;
    }
    if (audio.dataset.pageMutedByToggle) {
      audio.muted = false;
      delete audio.dataset.pageMutedByToggle;
    }
  }

  _syncPageMediaMuteAfterPlay() {
    if (this.model?.pageMediaMuted) {
      this.applyPageMediaMute(true);
    }
  }

  _getMusicEventSourceId() {
    return this.dbConfig?.id ?? this.getId?.() ?? this.id ?? this.unique_id;
  }

  _fireMusicEvent(eventType) {
    if (!eventType || this.model?.editMode) return;
    const sourceId = this._getMusicEventSourceId();
    if (sourceId == null) return;
    this.model?.view?._executeEventActions?.(sourceId, eventType);
  }

  _onMusicPlaybackStopped() {
    if (!this._musicPlaybackActive) return;
    this._musicPlaybackActive = false;
    this._fireMusicEvent('stoppedPlaying');
  }

  _ensureAudioPlaybackListeners() {
    const audio = this._ensureAudioElement();
    if (!audio || audio.dataset.musicLifecycleBound) return;
    audio.dataset.musicLifecycleBound = '1';
    audio.addEventListener('ended', () => {
      this._onMusicPlaybackStopped();
    });
  }

  _syncAudioSource() {
    const audio = this._ensureAudioElement();
    if (!audio) return;
    this._ensureAudioPlaybackListeners();
    const src = this.dbConfig?.audioUrl || '';
    if (src && audio.src !== src) {
      audio.src = src;
    }
    if (!src && audio.src) {
      audio.removeAttribute('src');
      audio.load();
    }
  }

  playOnce() {
    this._syncVisibilityForMode();
    this._syncAudioSource();
    if (!this.audioEl || !this.audioEl.src) return;
    this.audioEl.loop = false;
    this.audioEl.currentTime = 0;
    this._musicPlaybackActive = true;
    this.audioEl.play?.().catch(() => { });
    this._syncPageMediaMuteAfterPlay();
    this._fireMusicEvent('playOnce');
  }

  playLoop() {
    this._syncVisibilityForMode();
    this._syncAudioSource();
    if (!this.audioEl || !this.audioEl.src) return;
    this.audioEl.loop = true;
    this._musicPlaybackActive = true;
    this.audioEl.play?.().catch(() => { });
    this._syncPageMediaMuteAfterPlay();
    this._fireMusicEvent('playLoop');
  }

  stopPlaying() {
    this._syncVisibilityForMode();
    if (!this.audioEl) return;
    const wasActive = this._musicPlaybackActive;
    this.audioEl.pause?.();
    try {
      this.audioEl.currentTime = 0;
    } catch (_) { }
    if (wasActive) {
      this._onMusicPlaybackStopped();
    }
  }

  setDbConfig(config) {
    super.setDbConfig(config);
    this._syncVisibilityForMode();
    this._syncAudioSource();
  }
}

export { VRMusicObject };
